'use client';

import type { JSX } from 'react';
import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import type { DateValue } from '@/lib/discover/url-state';

import { CustomRangePopover } from './CustomRangePopover';
import { ACTIVE_PILL_CLASS } from './pill-active-class';

const ISO_DATE_RANGE = /^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/;

interface PresetPill {
  key: 'tonight' | 'tomorrow' | 'weekend' | 'next7' | 'next30';
  label: string;
}

/**
 * Preset pills mapping to `date` URL values.
 *
 * "Tonight" semantics (resolved server-side in M7.2 / WEB-58): the city's
 * local timezone, today, 17:00 → 04:00 the next day. Keep this comment in
 * sync with the server resolver so the client and API agree on the same
 * day-window boundary.
 */
const PRESETS: readonly PresetPill[] = [
  { key: 'tonight', label: 'Tonight' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'weekend', label: 'This weekend' },
  { key: 'next7', label: 'Next 7 days' },
  { key: 'next30', label: 'Next 30 days' },
] as const;

/**
 * Base class list for a single date pill (matches DS v1.1.0
 * `docs/design-system/1.1.0/templates/app/discover.html:188`).
 *
 * We render a plain `<button>` (not the shadcn `Button variant="outline"`)
 * for two reasons relevant to WEB-348:
 *   1. The outline variant ships `hover:bg-accent`/`hover:text-accent-foreground`
 *      which fights the active fill on touch devices (tap leaves `:hover`
 *      stuck, layering the variant's amber hover over an already-coloured
 *      active pill → the visual "hang" reported in the punchlist).
 *   2. The outline variant ships `transition-all` over 150ms+ which animates
 *      every property — combined with the URL → React round-trip latency on
 *      `router.replace()`, the active-state swap visibly lags click. The
 *      template's `transition-colors` over 120ms (compositor-friendly: only
 *      `color`, `background-color`, and `border-color`) feels instantaneous.
 *
 * The `@media (hover: hover)` guard ensures hover styling only paints on
 * devices with a real hover pointer — eliminating the stuck-hover artifact
 * on iOS Safari / Android Chrome where `:hover` persists until next tap.
 *
 * NB: the active-state class list comes from `pill-active-class.ts`. Hover
 * styling here intentionally only touches the border so it can't visually
 * collide with an active fill.
 */
const PILL_BASE_CLASS = cn(
  // Layout — template uses `padding: 8px 12px`, text-align left.
  // WEB-1076 / FRE-1379: 36px is the sheet's visual height on both pointers;
  // the 44px touch floor is the coarse-pointer `.a-hit-floor` `::before`
  // (atoms.css), so paint never grows for touch.
  'a-hit-floor inline-flex items-center justify-start',
  'min-h-9 desktop:min-h-0 px-3 py-2',
  'rounded-md',
  // Surface — transparent default, theme border so light/dark both work.
  'border border-border bg-transparent',
  // Typography — mono, 11px (text-[11px]), wide tracking per template.
  'font-mono text-micro tracking-[0.04em] text-muted-foreground',
  // Motion — colour transitions only, 120ms per template.
  // `transition-colors` is compositor-friendly (no layout thrash) so the
  // active-state paint lands on the very next frame after the URL change.
  'transition-colors duration-[120ms]',
  // Hover — only on real hover pointers; only touches border + text colour
  // so it cannot fight the active fill. Wrapped via the `hover:` arbitrary
  // media variant so touch devices don't stick on the hover style.
  '[@media(hover:hover)]:hover:border-clt-skyline [@media(hover:hover)]:hover:text-foreground',
  // Focus ring uses the same skyline token so the focus + active states are
  // visually consistent and don't compositor-fight on Safari.
  'outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  // Disabled / pointer hygiene shared with the rest of the Button primitive.
  'cursor-pointer disabled:pointer-events-none disabled:opacity-50',
);

function isCustomRange(value: DateValue | undefined): boolean {
  return typeof value === 'string' && ISO_DATE_RANGE.test(value);
}

export interface DiscoverDatePillsViewProps {
  /** Current `date` URL value (preset key or `YYYY-MM-DD..YYYY-MM-DD`). */
  value?: DateValue;
  /** Called with the new `date` URL value when a pill is clicked. */
  onChange: (next: DateValue | undefined) => void;
  /** Optional class for the outer container. */
  className?: string;
}

/**
 * Pure presentational view for the date filter pills. Six options:
 * `tonight`, `tomorrow`, `weekend`, `next7`, `next30`, plus a custom range
 * popover that emits `YYYY-MM-DD..YYYY-MM-DD`.
 *
 * The active pill mirrors `value`; if `value` matches the ISO range pattern
 * the "Custom…" pill is highlighted instead.
 *
 * Layout (DS v1.1.0 template, WEB-348): two-column CSS grid, 8px gap, small
 * radius. The custom-range trigger occupies the sixth grid cell. Spacing is
 * `gap-2` (8px) between the 36px pills; their coarse-pointer hit boxes come
 * from `.a-hit-floor` (WEB-1076).
 *
 * Use this view in Storybook and unit tests; the Connected component below
 * wires it to `useDiscoverParams`.
 */
export function DiscoverDatePillsView({
  value,
  onChange,
  className,
}: DiscoverDatePillsViewProps): React.JSX.Element {
  const customActive = isCustomRange(value);

  return (
    <fieldset data-testid="discover-date-pills" className={cn('grid grid-cols-2 gap-2', className)}>
      <legend className="sr-only">Date</legend>
      {/*
        Date presets are single-select (one preset OR a custom range, never
        multiple), so `radiogroup` is the semantically correct ARIA pattern —
        each preset is a `radio` carrying `aria-checked`, not an independent
        toggle button (`aria-pressed`). Audit 2026-06-10 #8 / WEB-287.

        The custom-range trigger is intentionally OUTSIDE this radiogroup: it
        is a popover disclosure (Radix sets `aria-expanded`/`aria-haspopup`),
        which `role="radio"` does not permit (axe `aria-allowed-attr`). It
        stays a toggle button with `aria-pressed` reflecting whether the
        active `date` value is a custom range.

        `display: contents` lets the radiogroup wrapper drop out of the grid
        flow so the five preset buttons + the custom popover trigger all sit
        directly inside the two-column grid.
      */}
      <div role="radiogroup" aria-label="Date preset" className="contents">
        {PRESETS.map((preset) => {
          const isActive = value === preset.key;
          return (
            <button
              key={preset.key}
              type="button"
              role="radio"
              className={cn(PILL_BASE_CLASS, isActive && ACTIVE_PILL_CLASS)}
              aria-checked={isActive}
              onClick={() => onChange(preset.key)}
              data-testid={`discover-date-pill-${preset.key}`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <CustomRangePopover
        value={customActive ? value : undefined}
        isActive={customActive}
        onConfirm={(range) => onChange(range)}
        triggerClassName={PILL_BASE_CLASS}
      />
    </fieldset>
  );
}

export interface DiscoverDatePillsProps {
  /** Optional class for the outer container. */
  className?: string;
}

/**
 * Connected `DiscoverDatePills` — reads/writes the `date` URL param via
 * `useDiscoverParams`. Click a preset to set `?date=tonight` (etc.); confirm
 * a custom range to set `?date=2026-04-01..2026-04-05`. The active state
 * mirrors the URL on mount and on remote changes (e.g. deep-link to
 * `?date=tomorrow`).
 *
 * Optimistic active-state (WEB-348):
 *   - The visible active pill is held in local state and updated synchronously
 *     on click, so the swap paints on the very next frame without waiting for
 *     `router.replace()` → RSC fetch → `useSearchParams()` to round-trip.
 *   - The local state is reconciled to the URL value on every render via a
 *     plain assignment guarded by a ref — this lets deep-links, back/forward
 *     navigation, and remote URL changes still mirror correctly without
 *     requiring `useOptimistic` (React 19-only; this app's `react-dom` is on
 *     18.3.1).
 *   - This is the root fix for the punchlist hang: the visual lag came from
 *     the URL → React round-trip (RCA #2 in the WEB-348 audit). The View
 *     dropping the shadcn `Button variant="outline"` removes the stuck-:hover
 *     and `transition-all` artifacts (RCA #1), but those alone don't shrink
 *     the click → paint gap to a single frame — only optimistic state does.
 */
export function DiscoverDatePills({ className }: DiscoverDatePillsProps): React.JSX.Element {
  const { params, setParams } = useDiscoverParams();

  // `displayDate` is what the View renders as active. We seed it from the URL
  // on mount and then keep it in sync with the URL whenever the URL changes
  // from any source (back/forward, remote update, another component writing
  // through `useDiscoverParams`). On click, we update it synchronously _and_
  // call `setParams`, so the visible active pill swaps on the same frame as
  // the click instead of waiting for the URL round-trip.
  const [displayDate, setDisplayDate] = React.useState<DateValue | undefined>(params.date);
  const lastSyncedUrlDate = React.useRef<DateValue | undefined>(params.date);
  React.useEffect(() => {
    if (params.date !== lastSyncedUrlDate.current) {
      lastSyncedUrlDate.current = params.date;
      setDisplayDate(params.date);
    }
  }, [params.date]);

  const handleChange = React.useCallback(
    (next: DateValue | undefined) => {
      setDisplayDate(next);
      // Track what we just pushed so the URL→state reconcile effect doesn't
      // bounce it back on the next render (the URL update lands one tick
      // later).
      lastSyncedUrlDate.current = next;
      setParams({ date: next });
    },
    [setParams],
  );
  return (
    <DiscoverDatePillsView value={displayDate} onChange={handleChange} className={className} />
  );
}

/**
 * @internal exported only for siblings (`CustomRangePopover`) that need to
 * mount the same base class list on a non-`<button>` element (Radix
 * `PopoverTrigger asChild` renders an arbitrary element). Not part of the
 * public API.
 */
export { PILL_BASE_CLASS };
